/**
 * Stake animation CSS keyframes and hook used by the USDv positions page
 * components. Extracted verbatim from prototype/pages/USDv/USDvPositionsPage.tsx
 * so that PortfolioCard, TradeWidget, WithdrawTableV3, etc. can share the same
 * animation primitives.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface StakeAnimState {
    borderIn: boolean;
    borderOut: boolean;
    lineIn: boolean;
    lineOut: boolean;
    bump: boolean;
    count: boolean;
    triggerKey: number;
}

export const STAKE_ANIM_IDLE: StakeAnimState = {
    borderIn: false,
    borderOut: false,
    lineIn: false,
    lineOut: false,
    bump: false,
    count: false,
    triggerKey: 0
};

export const ANIM_CSS = `
@keyframes stake-top-in {
    0%   { clip-path: inset(0 50% 0 50%); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes stake-top-left-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(0 0 0 100%); }
}
@keyframes stake-top-right-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(0 100% 0 0); }
}
@keyframes stake-sides-in {
    0%   { clip-path: inset(0 0 100% 0); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes stake-sides-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(100% 0 0 0); }
}
@keyframes stake-bottom-in {
    0%   { clip-path: inset(0 50% 0 50%); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes stake-bottom-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(0 50% 0 50%); }
}
@keyframes stake-line-in {
    0%   { clip-path: inset(0 0 100% 0); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes stake-line-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(100% 0 0 0); }
}
@keyframes page-fade-up {
    0%   { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
}
@keyframes fade-in {
    0%   { opacity: 0; }
    100% { opacity: 1; }
}
.widget-form-loading {
    color: transparent !important;
}
.widget-form-loading img,
.widget-form-loading input,
.widget-form-loading svg {
    opacity: 0 !important;
}
@keyframes cooldown-fill {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
}
@keyframes focus-scale-in {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
}
@keyframes focus-scale-out {
    0%   { transform: scaleX(1); }
    100% { transform: scaleX(0); }
}
@keyframes stake-bump {
    0%   { transform: translateY(0); }
    45%  { transform: translateY(3px); }
    75%  { transform: translateY(-1px); }
    100% { transform: translateY(0); }
}
`;

export function useStakeAnimation(parsed: number): StakeAnimState {
    const [state, setState] = useState<StakeAnimState>(STAKE_ANIM_IDLE);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const borderTriggeredRef = useRef(false);
    // Tracks whether the line/bump/count sequence has started emitting state
    // changes. While false, there are no transient flags to reset, so we can
    // skip the reset setState and avoid a render per keystroke.
    const inSequenceRef = useRef(false);

    const clearSequence = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
    }, []);

    useEffect(() => {
        if (parsed === 0) {
            clearSequence();
            if (borderTriggeredRef.current) {
                // Play border-out, then reset
                borderTriggeredRef.current = false;
                inSequenceRef.current = false;
                setState((s) => ({
                    ...s,
                    borderOut: true,
                    lineIn: false,
                    lineOut: false,
                    bump: false,
                    count: false
                }));
                const t = setTimeout(() => {
                    setState(STAKE_ANIM_IDLE);
                }, 900);
                timeoutsRef.current = [t];
            } else {
                setState(STAKE_ANIM_IDLE);
            }
            return;
        }

        // Border-in: fires once on first input, stays until border-out
        if (!borderTriggeredRef.current) {
            borderTriggeredRef.current = true;
            const key = Date.now();
            setState({
                borderIn: true,
                borderOut: false,
                lineIn: false,
                lineOut: false,
                bump: false,
                count: false,
                triggerKey: key
            });
        }

        // Line/bump/count: debounced — retriggers on each pause.
        // Only reset transient flags if the sequence has actually started
        // (otherwise there's nothing to clear and the setState is a wasted
        // render on every keystroke).
        clearSequence();
        if (inSequenceRef.current) {
            inSequenceRef.current = false;
            setState((s) => ({ ...s, lineIn: false, lineOut: false, bump: false, count: false }));
        }

        debounceRef.current = setTimeout(() => {
            const timers: ReturnType<typeof setTimeout>[] = [];
            inSequenceRef.current = true;

            // t=0: line starts (75ms)
            setState((s) => ({ ...s, lineIn: true }));

            // t=75: line arrives — bump (200ms) + count start
            timers.push(
                setTimeout(() => {
                    setState((s) => ({ ...s, bump: true, count: true }));
                }, 75)
            );

            // t=275: bump done, swap lineIn → lineOut
            timers.push(
                setTimeout(() => {
                    setState((s) => ({ ...s, bump: false, lineIn: false, lineOut: true }));
                }, 275)
            );

            // t=475: line-out done. Drop `count` back to false so the next
            // typing burst's `count` flip true→false→true acts as a fresh
            // pulse signal (instead of staying latched true forever, which
            // caused subsequent count-ups to fire on target change rather
            // than waiting for the bump).
            timers.push(
                setTimeout(() => {
                    setState((s) => ({ ...s, lineOut: false, count: false }));
                    inSequenceRef.current = false;
                }, 475)
            );

            timeoutsRef.current = timers;
        }, 500);

        return clearSequence;
    }, [parsed, clearSequence]);

    return state;
}
